import { useRef, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import * as R from 'ramda'
import s from './styles.module.css'
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import { useMediaQuery } from 'hooks/use-media-query'
import useDynamicMaxHeight from 'hooks/use-dynamic-max-height'

export interface SelectOption {
  value: string
  label: string
}

interface GroupedOption {
  label: string
  options: SelectOption[]
}

export const MOST_COMMON_DIAGNOSES = [
  { code: 'R73.03', name: 'Pre-Diabetes' },
  { code: 'E11.9', name: 'Type 2 Diabetes' },
]

export const OBESITY_DIAGNOSES = [
  { code: 'E66.811', name: 'Obesity', hidden: true },
  {
    code: 'E66.811',
    name: 'Obesity, class 1 BMI between 30.0 and 34.9',
    coachOnly: true,
  },
  {
    code: 'E66.812',
    name: 'Obesity, class 2 BMI between 35.0 and 39.9',
    coachOnly: true,
  },
  {
    code: 'E66.813',
    name: 'Obesity, class 3 BMI of 40.0 or higher',
    coachOnly: true,
  },
  {
    code: 'E66.89',
    name: 'Other obesity not elsewhere classified',
    coachOnly: true,
  },
  {
    code: 'E66.01',
    name: 'Morbid (severe) obesity due to excess calories',
    coachOnly: true,
  },
  { code: 'E66.3', name: 'Overweight', hidden: true },
  { code: 'Z68.25', name: 'Body mass index (BMI) 25 adult', coachOnly: true },
  { code: 'Z68.26', name: 'Body mass index (BMI) 26 adult', coachOnly: true },
  { code: 'Z68.27', name: 'Body mass index (BMI) 27 adult', coachOnly: true },
  { code: 'Z68.28', name: 'Body mass index (BMI) 28 adult', coachOnly: true },
  { code: 'Z68.29', name: 'Body mass index (BMI) 29 adult', coachOnly: true },
  { code: 'Z68.30', name: 'Body mass index (BMI) 30 adult', coachOnly: true },
  { code: 'Z68.31', name: 'Body mass index (BMI) 31 adult', coachOnly: true },
  { code: 'Z68.32', name: 'Body mass index (BMI) 32 adult', coachOnly: true },
  { code: 'Z68.33', name: 'Body mass index (BMI) 33 adult', coachOnly: true },
  { code: 'Z68.34', name: 'Body mass index (BMI) 34 adult', coachOnly: true },
  { code: 'Z68.35', name: 'Body mass index (BMI) 35 adult', coachOnly: true },
  {
    code: 'Z68.36',
    name: 'Body mass index [BMI] 36.0-36.9 adult',
    coachOnly: true,
  },
  {
    code: 'Z68.37',
    name: 'Body mass index (BMI) 37.0-37.9 adult',
    coachOnly: true,
  },
  {
    code: 'Z68.38',
    name: 'Body mass index [BMI] 38.0-38.9 adult',
    coachOnly: true,
  },
  {
    code: 'Z68.39',
    name: 'Body mass index [BMI] 39.0-39.9 adult',
    coachOnly: true,
  },
  {
    code: 'Z68.41',
    name: 'Body mass index [BMI] 40.0-44.9, adult',
    coachOnly: true,
  },
  {
    code: 'Z68.42',
    name: 'Body mass index [BMI] 45.0-49.9, adult',
    coachOnly: true,
  },
  {
    code: 'Z68.43',
    name: 'Body mass index [BMI] 50.0-59.9, adult',
    coachOnly: true,
  },
  {
    code: 'Z68.44',
    name: 'Body mass index [BMI] 60.0-69.9, adult',
    coachOnly: true,
  },
  {
    code: 'Z68.45',
    name: 'Body mass index [BMI] 70 or greater, adult',
    coachOnly: true,
  },
]
export const DIABETES_DIAGNOSES = [
  { code: 'R73.03', name: 'Pre-Diabetes' },
  { code: 'E10.9', name: 'Type 1 Diabetes' },
  { code: 'E11.9', name: 'Type 2 Diabetes' },
  { code: 'O24.419', name: 'Gestational diabetes' },
]

export const CARDIOVASCULAR_DIAGNOSES = [
  { code: 'E78.5', name: 'High Cholesterol' },
  { code: 'I10', name: 'Hypertension' },
  { code: 'I25.10', name: 'Heart Disease' },
  { code: 'I13.0', name: 'Heart & Kidney Disease' },
]

export const KIDNEY_DIAGNOSES = [
  { code: 'N18.3', name: 'Moderate Kidney Disease, Stage 3' },
  { code: 'N18.4', name: 'Severe Kidney Disease, Stage 4' },
  { code: 'N18.5', name: 'Severe Kidney Disease, Stage 5' },
  { code: 'Z99.2', name: 'Kidney Disease on Dialysis' },
  { code: 'I13.0', name: 'Heart & Kidney Disease' },
]

export const GASTROINTESTINAL_DIAGNOSES = [
  { code: 'K58.9', name: 'IBS' },
  { code: 'K90.0', name: 'Celiac Disease' },
  { code: 'K21.9', name: 'GERD' },
]

export const EATING_DISORDERS_DIAGNOSES = [
  { code: 'F50.01', name: 'Anorexia, Restricting Type' },
  { code: 'F50.02', name: 'Anorexia, Binge-Eating/Purging Type' },
  { code: 'F50.2', name: 'Bulimia' },
]

export const ALLERGIES_DIAGNOSES = [
  { code: 'Z91.011', name: 'Dairy Allergy' },
  { code: 'Z91.012', name: 'Egg Allergy' },
  { code: 'Z91.02', name: 'Food Additive Allergy' },
  { code: 'Z91.014', name: 'Meat Allergy' },
  { code: 'Z91.010', name: 'Peanut Allergy' },
  { code: 'Z91.013', name: 'Seafood Allergy' },
]

export const getAllDiagnoses = (includeCoachOnly = false) => {
  const allDiagnoses = [
    ...OBESITY_DIAGNOSES,
    ...DIABETES_DIAGNOSES,
    ...CARDIOVASCULAR_DIAGNOSES,
    ...KIDNEY_DIAGNOSES,
    ...GASTROINTESTINAL_DIAGNOSES,
    ...EATING_DISORDERS_DIAGNOSES,
    ...ALLERGIES_DIAGNOSES,
  ]

  return includeCoachOnly
    ? allDiagnoses
    : R.filter(
        diagnosis => !diagnosis.coachOnly && !diagnosis.hidden,
        allDiagnoses
      )
}

export const getAllDiagnosesByCode = (includeCoachOnly = false) => {
  return R.indexBy(R.prop('code'), getAllDiagnoses(includeCoachOnly))
}

/**
 * Convert an array of diagnosis codes to SelectOption array
 * @param codes Array of diagnosis codes
 * @returns Array of SelectOption objects
 */
const codesToSelectOptions = (
  codes: string[],
  includeCoachOnly: boolean
): SelectOption[] => {
  return codes
    .map(code => {
      const allDiagnoses = getAllDiagnosesByCode(includeCoachOnly)
      const diagnosis = allDiagnoses[code]

      if (diagnosis) {
        return {
          value: diagnosis.code,
          label: diagnosis.name,
        }
      }
      return null
    })
    .filter(Boolean) as SelectOption[]
}

/**
 * Find select options for an array of diagnosis codes
 * @param codes Array of diagnosis codes to find
 * @returns Array of SelectOption objects that match the codes
 */
const findSelectOptionsByCodes = (
  codes: string[],
  includeCoachOnly: boolean
): SelectOption[] => {
  return codesToSelectOptions(codes, includeCoachOnly)
}

const DIAGNOSIS_OPTIONS: GroupedOption[] = [
  {
    label: 'Most Common',
    options: MOST_COMMON_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Obesity',
    options: OBESITY_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
      coachOnly: diagnosis.coachOnly,
      hidden: diagnosis.hidden,
    })),
  },
  {
    label: 'Diabetes',
    options: DIABETES_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Cardiovascular',
    options: CARDIOVASCULAR_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Kidney Disease',
    options: KIDNEY_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Gastrointestinal Disorders',
    options: GASTROINTESTINAL_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Eating Disorders',
    options: EATING_DISORDERS_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Allergies',
    options: ALLERGIES_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
]

const DiagnosisSelect = ({
  value,
  onChange,
  placeholder = undefined,
  className = '',
  shouldLimitMultiValue = true,
  includeCoachOnly = false,
  useMobileBottomSheet = false,
}: {
  value: string[]
  onChange: (selectedOptions: SelectOption[]) => void
  placeholder?: string
  className?: string
  shouldLimitMultiValue?: boolean
  includeCoachOnly?: boolean
  useMobileBottomSheet?: boolean
}) => {
  const selectedOptions = findSelectOptionsByCodes(
    value || [],
    includeCoachOnly
  )
  const isMobile = useMediaQuery('(max-width: 425px)')
  const isTablet = useMediaQuery('(max-width: 768px)')
  const valueContainerRef = useRef<HTMLDivElement>(null)
  const justSelectedRef = useRef(false)

  // Calculate maxHeight for non-bottom-sheet mobile
  const dropdownMaxHeight = useDynamicMaxHeight(300)

  // Bottom sheet drag-to-dismiss state
  const [dragOffset, setDragOffset] = useState(0)
  const dragStartY = useRef(0)
  const dragOffsetRef = useRef(0)
  const listboxButtonRef = useRef<HTMLButtonElement>(null)

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY
    const diff = currentY - dragStartY.current
    // Only allow dragging down
    if (diff > 0) {
      dragOffsetRef.current = diff
      setDragOffset(diff)
    }
  }

  const handleTouchEnd = () => {
    // If dragged more than 100px, dismiss
    if (dragOffsetRef.current > 100) {
      dragOffsetRef.current = 0
      setDragOffset(0)
      // Close by clicking the button
      listboxButtonRef.current?.click()
    } else {
      // Snap back
      dragOffsetRef.current = 0
      setDragOffset(0)
    }
  }

  // Filter options based on includeCoachOnly
  const filteredGroups = DIAGNOSIS_OPTIONS.map(group => ({
    ...group,
    options: group.options.filter(option => {
      if ((option as any).hidden) {
        return false
      }
      if (includeCoachOnly) {
        return true
      }
      return !(option as any).coachOnly
    }),
  })).filter(group => group.options.length > 0)

  // Handle option selection - Listbox with multiple handles this automatically
  const handleSelect = (newSelected: SelectOption[]) => {
    onChange(newSelected)

    // On mobile, mark that we just made a selection
    if (isMobile) {
      justSelectedRef.current = true
      setTimeout(() => {
        justSelectedRef.current = false
      }, 100)
    }

    // Scroll to right after selection
    setTimeout(() => {
      if (valueContainerRef.current) {
        valueContainerRef.current.scrollLeft =
          valueContainerRef.current.scrollWidth
      }
    }, 0)
  }

  // Get selected values set for quick lookup
  const selectedValues = new Set(selectedOptions.map(opt => opt.value))

  // Remove selected item
  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const newSelected = selectedOptions.filter(opt => opt.value !== optionValue)
    onChange(newSelected)
  }

  // Get display values for selected items (with limiting for mobile/tablet)
  const getDisplayValues = () => {
    if (!shouldLimitMultiValue) {
      return selectedOptions
    }

    if (isMobile && selectedOptions.length > 2) {
      return selectedOptions.slice(0, 2)
    }

    if (isTablet && !isMobile && selectedOptions.length > 4) {
      return selectedOptions.slice(0, 4)
    }

    return selectedOptions
  }

  const displayValues = getDisplayValues()
  const hasMoreValues =
    shouldLimitMultiValue &&
    ((isMobile && selectedOptions.length > 2) ||
      (isTablet && !isMobile && selectedOptions.length > 4))

  // Auto-scroll to right when selections change
  useEffect(() => {
    if (valueContainerRef.current && selectedOptions.length > 0) {
      requestAnimationFrame(() => {
        if (valueContainerRef.current) {
          valueContainerRef.current.scrollLeft =
            valueContainerRef.current.scrollWidth
        }
      })
    }
  }, [selectedOptions.length])

  return (
    <div className={cn(s.selectContainer, className)}>
      <Listbox
        value={selectedOptions}
        onChange={handleSelect}
        by="value"
        multiple>
        {({ open }) => (
          <>
            <ListboxButton ref={listboxButtonRef} className={s.listboxButton}>
              <div
                ref={valueContainerRef}
                className={s.valueContainer}
                onClick={e => {
                  // Prevent button click when clicking remove buttons
                  if ((e.target as HTMLElement).closest(`.${s.removeButton}`)) {
                    e.stopPropagation()
                  }
                }}>
                {selectedOptions.length === 0 ? (
                  <span className={s.placeholder}>
                    {placeholder || 'Select...'}
                  </span>
                ) : (
                  <>
                    {displayValues.map((option, index) => (
                      <div key={option.value} className={s.selectedValue}>
                        <span className={s.selectedValueLabel}>
                          {option.label}
                          {hasMoreValues && index === displayValues.length - 1
                            ? ', ...'
                            : index < selectedOptions.length - 1
                            ? ', '
                            : ''}
                        </span>
                        <button
                          type="button"
                          className={s.removeButton}
                          onClick={e => handleRemove(option.value, e)}
                          aria-label={`Remove ${option.label}`}>
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M9 3L3 9M3 3L9 9"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                    {hasMoreValues && (
                      <span className={s.moreValues}>
                        +{selectedOptions.length - displayValues.length} more
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Dropdown indicator */}
              <span className={s.dropdownIndicator}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}>
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </ListboxButton>

            {/* Options dropdown - bottom sheet on mobile if enabled */}
            {isMobile && open && useMobileBottomSheet ? (
              createPortal(
                <>
                  <div
                    className={s.backdrop}
                    style={{ opacity: Math.max(0, 1 - dragOffset / 200) }}
                    onClick={e => e.stopPropagation()}
                  />
                  <ListboxOptions
                    static
                    className={cn(s.listboxOptions, s.bottomSheet)}
                    style={{
                      transform: `translateY(${dragOffset}px)`,
                      transition:
                        dragOffset > 0 ? 'none' : 'transform 0.2s ease-out',
                    }}>
                    <div
                      className={s.bottomSheetHeader}
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}>
                      <div className={s.bottomSheetHandle} />
                      <span className={s.bottomSheetTitle}>
                        Select Health Conditions
                      </span>
                    </div>
                    {filteredGroups.map(group => (
                      <div key={group.label}>
                        <div className={s.groupHeading}>{group.label}</div>
                        {group.options.map(option => {
                          const isSelected = selectedValues.has(option.value)
                          return (
                            <ListboxOption
                              key={option.value}
                              value={option}
                              className={({ active, focus }) =>
                                cn(s.listboxOption, {
                                  [s.listboxOptionActive]: active || focus,
                                  [s.listboxOptionSelected]: isSelected,
                                })
                              }>
                              <div className={s.optionContent}>
                                <div
                                  className={cn(s.checkbox, {
                                    [s.selected]: isSelected,
                                  })}>
                                  {isSelected && (
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 16 16"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg">
                                      <path
                                        d="M13.3333 4L6 11.3333L2.66667 8"
                                        stroke="white"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  )}
                                </div>
                                <span>{option.label}</span>
                              </div>
                            </ListboxOption>
                          )
                        })}
                      </div>
                    ))}
                  </ListboxOptions>
                </>,
                document.body
              )
            ) : (
              <ListboxOptions
                className={s.listboxOptions}
                style={{
                  maxHeight: isMobile ? dropdownMaxHeight : undefined,
                }}>
                {filteredGroups.map(group => (
                  <div key={group.label}>
                    <div className={s.groupHeading}>{group.label}</div>
                    {group.options.map(option => {
                      const isSelected = selectedValues.has(option.value)
                      return (
                        <ListboxOption
                          key={option.value}
                          value={option}
                          className={({ active, focus }) =>
                            cn(s.listboxOption, {
                              [s.listboxOptionActive]: active || focus,
                              [s.listboxOptionSelected]: isSelected,
                            })
                          }>
                          <div className={s.optionContent}>
                            <div
                              className={cn(s.checkbox, {
                                [s.selected]: isSelected,
                              })}>
                              {isSelected && (
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M13.3333 4L6 11.3333L2.66667 8"
                                    stroke="white"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </div>
                            <span>{option.label}</span>
                          </div>
                        </ListboxOption>
                      )
                    })}
                  </div>
                ))}
              </ListboxOptions>
            )}
          </>
        )}
      </Listbox>
    </div>
  )
}

export default DiagnosisSelect
